import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { getUniversityOptions, searchUniversities } from '../api.js';
import { useDebouncedValue } from './useDebouncedValue.js';
import { useDismissingNote } from './useDismissingNote.js';
import { pruneInvalidSelections, universityFields, formatCleanupMessage } from '../filterDependency.js';

/**
 * The University search state machine.
 *
 * Same shape as useProgramSearch — one reducer, every control resets the page
 * and drops the accumulated rows, only "Load more" opts out.
 *
 * Universities are a first-class index built from the same Mongo `universities`
 * collection the reference product reads, so every reference filter is here:
 * name, university-country, course intake, course type, location (campus),
 * highest qualification, academic gap, "interview required", "accepts MOI", and
 * "tuition below 15k". Nationality scope is applied server-side from the
 * session, exactly as for programs — this tab offers no nationality control.
 */
export const EMPTY_FILTERS = {
  q: '',
  countries: [],
  // "University Name" dropdown — the display name(s); the server matches it on
  // an indexed single-token key, exactly as Search Programs does. Separate from
  // `q` (the free-text "Search by name" box): this is an exact pick from the
  // in-scope list, that is a substring search.
  universities: [],
  // Exact intakes, chosen by id — the same control Search Programs uses.
  // "January/February 2027" is its own intake with its own id, not a coarse
  // "Jan 2027" recombined from a month picker and a year select.
  //
  // The "Intake status: Active" filter is deliberately Programs-only — the
  // Universities tab has no such control and never sends `intakeStatus`.
  intakeIds: [],
  courseTypeIds: [],
  campusIds: [],
  educationLevels: [],
  gapYear: '',
  moiAccepted: false,
  interviewRequired: false,
  tuitionBelow15k: false,
};

export const INITIAL = { filters: EMPTY_FILTERS, page: 1, appending: false };

function toggleIn(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** De-duplicate by id when "Load more" appends — same guard as the course list. */
function appendUnseen(previous, incoming) {
  const seen = new Set(previous.map((u) => u.id));
  return previous.concat(incoming.filter((u) => !seen.has(u.id)));
}

export function reducer(state, action) {
  const fresh = (patch) => ({ ...state, ...patch, page: 1, appending: false });
  const withFilters = (patch) => fresh({ filters: { ...state.filters, ...patch } });

  switch (action.type) {
    case 'SET_QUERY':
      return withFilters({ q: action.value });
    case 'SET_COUNTRY':
      return withFilters({ countries: action.value ? [action.value] : [] });
    case 'SET_UNIVERSITY':
      return withFilters({ universities: action.value ? [action.value] : [] });
    case 'SET_LOCATION':
      return withFilters({ campusIds: action.value ? [action.value] : [] });
    case 'SET_COURSE_TYPE':
      return withFilters({ courseTypeIds: action.value ? [action.value] : [] });
    case 'SET_QUALIFICATION':
      return withFilters({ educationLevels: action.value ? [action.value] : [] });
    case 'SET_GAP_YEAR':
      return withFilters({ gapYear: action.value });
    case 'TOGGLE':
      return withFilters({ [action.key]: toggleIn(state.filters[action.key], action.value) });
    case 'SET_FLAG':
      return withFilters({ [action.key]: action.value });
    // See the same case in useProgramSearch.js's reducer — a follow-up dispatch
    // once a response has said which currently-held values are no longer
    // reachable, never a reason to block the change that produced it.
    case 'PRUNE_INVALID':
      return withFilters(action.patch);
    case 'CLEAR_ALL':
      return INITIAL;
    case 'LOAD_MORE':
      return { ...state, page: state.page + 1, appending: true };
    default:
      return state;
  }
}

const PER_PAGE = 12;

export function useUniversitySearch() {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  const [options, setOptions] = useState(null);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const debouncedQ = useDebouncedValue(state.filters.q, 200);

  const body = useMemo(() => ({
    q: debouncedQ,
    countries: state.filters.countries,
    universities: state.filters.universities,
    // Sent exactly as picked — no month/year recombination, and no active/
    // inactive intake filtering (that control is Programs-only).
    intakeIds: state.filters.intakeIds,
    courseLevelIds: state.filters.courseTypeIds,
    campusIds: state.filters.campusIds,
    educationLevels: state.filters.educationLevels,
    gapYear: state.filters.gapYear,
    moiAccepted: state.filters.moiAccepted,
    interviewRequired: state.filters.interviewRequired,
    tuitionBelow15k: state.filters.tuitionBelow15k,
    page: state.page,
    perPage: PER_PAGE,
  }), [state.filters, state.page, debouncedQ]);

  // Signature of everything that changes the RESULT SET, minus the page. While
  // it differs from the signature that produced the current `data`, that data is
  // for a previous query — see `stale` below, which the result bar reads so it
  // shows "…" instead of the old total under a just-changed filter.
  const bodyKey = useMemo(
    () => JSON.stringify({ ...state.filters, q: debouncedQ }),
    [state.filters, debouncedQ],
  );
  const [resultsKey, setResultsKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getUniversityOptions()
      .then((opts) => { if (!cancelled) setOptions(opts); })
      .catch(() => {
        if (cancelled) return;
        setError("We couldn't load the university search. Please refresh the page.");
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const requestId = useRef(0);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [cleanupNote, announceCleanup] = useDismissingNote();

  const run = useCallback(async (searchBody, { append, key, filters }) => {
    const id = requestId.current + 1;
    requestId.current = id;
    setLoading(true);
    try {
      const result = await searchUniversities(searchBody);
      if (requestId.current !== id) return;
      setData(result);
      setError(null);
      setRows((prev) => (append ? appendUnseen(prev, result.universities) : result.universities));
      if (key !== undefined) setResultsKey(key);

      // See the matching block in useProgramSearch.js's run() — same
      // mechanism, over this tab's own dependent fields.
      if (!append && filters) {
        const { patch, removed } = pruneInvalidSelections(filters, result.facets, universityFields(optionsRef.current));
        if (removed.length > 0) {
          dispatch({ type: 'PRUNE_INVALID', patch });
          announceCleanup(formatCleanupMessage(removed));
        }
      }
    } catch (err) {
      if (requestId.current !== id) return;
      setError(err.message);
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, [announceCleanup]);

  const appendingRef = useRef(false);
  appendingRef.current = state.appending;

  useEffect(() => {
    if (!options) return;
    run(body, { append: appendingRef.current, key: bodyKey, filters: state.filters });
  }, [body, options, run, bodyKey, state.filters]);

  return {
    filters: state.filters,
    options,
    data,
    rows,
    error,
    loading,
    // `data` is for a previous set of filters — the result bar shows "…" rather
    // than the stale count while the new search is in flight.
    stale: bodyKey !== resultsKey,
    dispatch,
    cleanupNote,
  };
}
